import DateInput from "@app/components/date-input.tsx";
import FiltersContainer from "@app/components/filters-container.tsx";
import InvoicePrintModal from "@app/components/modals/invoice/invoice-print-modal.tsx";
import PageTitle from "@app/components/page-title.tsx";
import { defaultInvoicingFilters } from "@app/constants/filters.defaults.tsx";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useInvoiceManagementData from "@app/data-hooks/use-invoice-management-data.ts";
import { RouteHelper } from "@app/helpers/route-helper.ts";
import { parseDate } from "@app/helpers/time-helper";
import useAuth from "@app/hooks/use-auth.ts";
import { invoicingFiltersStore } from "@app/stores/filters.store.ts";
import { InvoiceTableDefinition } from "@app/table-definitions/invoice-table.table-definition.tsx";
import { Button, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { VisibilityState } from "@tanstack/react-table";
import { DataTable, QueryStatusRouter, Select } from "bi-lib";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/_manager/invoice/")({
    beforeLoad: RouteHelper.ManagerAuthGuard,
    component: Component,
});

function Component() {
    const [invoiceId, setInvoiceId] = useState<number>();
    const [printDialogOpen, setPrintDialogOpen] = useState(false);

    const filters = useStore(invoicingFiltersStore);

    const navigate = useNavigate();

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
        invoiceNumber: filters.finalizeOnly,
        invoiceDate: filters.finalizeOnly,
    });

    const { invoiceAssignmentsList } = useAssignmentData();
    const { searchQuery, finalizeInvoice, unfinalizeInvoice, deleteInvoice } = useInvoiceManagementData({
        ...filters,
        start: filters?.start ? new Date(filters.start) : undefined,
        end: filters?.end ? new Date(filters.end) : undefined,
    });
    const { isAdmin } = useAuth();

    // Define the keys as a union of the actual property names of InvoiceModel
    type InvoiceSumKey = "invoiceAmount" | "otherExpensesAmount" | "travelExpenseAmount" | "timeCostAmount";
    const keysToSum: InvoiceSumKey[] = ["invoiceAmount", "otherExpensesAmount", "travelExpenseAmount", "timeCostAmount"];

    const totalSums = keysToSum.reduce((acc, key) => {
        acc[key] = searchQuery?.data
            ? searchQuery?.data.reduce((sum, item) => sum + (parseFloat(item[key] as unknown as string) || 0), 0)
            : 0;
        return acc;
    }, {} as Record<InvoiceSumKey, number>);

    return (
        <>
            <InvoicePrintModal
                open={printDialogOpen}
                onClose={() => {
                    setInvoiceId(undefined);
                    setPrintDialogOpen(false);
                }}
                invoiceId={invoiceId!}
            />
            <PageTitle>Invoice Management</PageTitle>
            <FiltersContainer onReset={() => invoicingFiltersStore.setState(() => defaultInvoicingFilters)}>
                <DateInput
                    label={"Start Date"}
                    value={filters.start}
                    onChange={(e) => {
                        const valueDate = parseDate(e);
                        const endDate = parseDate(filters.end);
                        if (!valueDate) {
                            invoicingFiltersStore.setState((prev) => ({ ...prev, start: e }));
                            return;
                        }
                        invoicingFiltersStore.setState((prev) => ({ ...prev, start: e }));
                        if (endDate && valueDate > endDate) {
                            invoicingFiltersStore.setState((prev) => ({ ...prev, end: e }));
                        }
                    }}
                />
                <DateInput
                    label={"End Date"}
                    value={filters.end}
                    onChange={(e) => {
                        const valueDate = parseDate(e);
                        const startDate = parseDate(filters.start);
                        if (!valueDate) {
                            invoicingFiltersStore.setState((prev) => ({ ...prev, end: e }));
                            return;
                        }
                        invoicingFiltersStore.setState((prev) => ({ ...prev, end: e }));
                        if (startDate && valueDate < startDate) {
                            invoicingFiltersStore.setState((prev) => ({ ...prev, start: e }));
                        }
                    }}
                />
                <QueryStatusRouter queryHook={invoiceAssignmentsList}>
                    <Select
                        label={"Assignments"}
                        sx={{ minWidth: 200 }}
                        value={filters.assignmentId}
                        onChange={(e) => invoicingFiltersStore.setState(() => ({ ...filters, assignmentId: e }))}
                        options={invoiceAssignmentsList.data?.map((x) => ({
                            label: x.displayName,
                            value: x.assignmentId,
                        }))}
                    />
                </QueryStatusRouter>
                <RadioGroup
                    value={filters.finalizeOnly}
                    onChange={(e) => {
                        const value = e.target.value == "true";
                        invoicingFiltersStore.setState(() => ({ ...filters, finalizeOnly: value }));
                        setColumnVisibility((prev) => ({ ...prev, invoiceNumber: value, invoiceDate: value }));
                    }}
                    row
                >
                    <FormControlLabel control={<Radio />} label={"Final"} value={true} />
                    <FormControlLabel control={<Radio />} label={"Draft"} value={false} />
                </RadioGroup>
            </FiltersContainer>
            <DataTable
                rowsPerPageOptions={[5000]}
                label={"Invoices"}
                actions={
                    <Link to={"/invoice/create"}>
                        <Button variant={"contained"}>Add new</Button>
                    </Link>
                }
                columns={InvoiceTableDefinition({
                    onPrint: (id) => {
                        setInvoiceId(id);
                        setPrintDialogOpen(true);
                    },
                    onUnfinalize: (id) =>
                        unfinalizeInvoice.mutate({
                            invoiceId: id,
                        }),
                    onDetailsView: (id) =>
                        navigate({ to: "/invoice/$invoiceId/edit", params: { invoiceId: id.toString() } }),
                    onFinalize: (id) =>
                        finalizeInvoice.mutate({
                            invoiceId: id,
                        }),
                    onDelete: (id) => deleteInvoice.mutate(id),
                    isAdmin: isAdmin,
                    rowCount: searchQuery.data?.length ?? 0,
                    totalSums: totalSums
                })}
                data={searchQuery.data ?? []}
                queryHook={searchQuery}
                columnVisibility={{
                    state: columnVisibility,
                    onChange: setColumnVisibility,
                }}
            />
        </>
    );
}
